import { getField, updateField } from 'vuex-map-fields';
import apiWarehouse from '@/services/api/warehouse';
import apiSheet from '@/services/api/sheet';
import edition from '@/services/api/edition';
import merge from 'lodash.merge';
import HttpStatus from 'http-status-codes';

const emptyAddWarehouse = () => {
    return {
        baseInfo: {
            baseParameters: [],
            showOptionalQuestion: null,
            wayForWarehouseOperation: null
        },
        locationData: {
            warehouseName: '',
            address: {
                locationName: null,
                street: null,
                city: null,
                flatNumber: null,
                placeNumber: null,
                province: null,
                rejon: null,
                postalCode: null
            },
            openingHours: {
                firstDayFrom: null,
                firstDayTo: null,
                secondDayFrom: null,
                secondDayTo: null,
                thirdDayFrom: null,
                thirdDayTo: null,
                additionalFirstDayChecked: 'no',
                additionalThirdDayChecked: 'yes'
            },
            directions: null
        },
        additionalParameters: {
            size: null,
            hasInternet: null,
            truckAccess: null,
            warehousePotential: null,
            needToCarryPackageUpstairs: null,
            warehouseEquipment: []
        },
        warehouseManager: {
            candidate: null,
            manager: null,
            phone: '',
            full_name: null
        },
        additionalNote: null
    };
};

const emptyAddSupportingWarehouse = () => {
    return {
        baseInfo: {
            supportingWarehouseType: null,
            baseParameters: [],
            showWayForWarehouseOperation: null,
            wayForWarehouseOperation: null,
            isLocationFree: null,
            whyThisLocation: '',
            cost: '',
            barter: ''
        },
        locationData: {
            warehouseName: '',
            address: {
                locationName: null,
                street: null,
                city: null,
                flatNumber: null,
                placeNumber: null,
                province: null,
                postalCode: null
            },
            openingHours: {
                firstDayFrom: null,
                firstDayTo: null,
                secondDayFrom: null,
                secondDayTo: null
            },
            directions: null
        },
        additionalParameters: {
            size: null,
            hasInternet: null,
            truckAccess: null,
            needToCarryPackageUpstairs: null,
            warehouseEquipment: [],
            warehouseOperation: null,
            showWarehouseOperation: null
        },
        warehouseManager: {
            manager: null,
            phone: '',
            full_name: null
        },
        additionalNote: ''
    };
};

const emptyAddress = () => {
    return {
        locationName: null,
        street: null,
        city: null,
        flatNumber: null,
        placeNumber: null,
        province: null,
        rejon: null,
        postalCode: null
    };
};

const emptyHours = () => {
    return {
        firstDayFrom: null,
        firstDayTo: null,
        secondDayFrom: null,
        secondDayTo: null,
        thirdDayFrom: null,
        thirdDayTo: null,
        additionalFirstDayChecked: 'no',
        additionalThirdDayChecked: 'yes'
    };
};

const WAREHOUSE_PARAMS_STORAGE_NAME = 'whlParams';

const paginationState = () => {
    const storage = localStorage.getItem(WAREHOUSE_PARAMS_STORAGE_NAME);
    return storage ? JSON.parse(storage) : { page: 1, amount: 20, filtersParams: null, amountAll: 0 };
};

export default {
    namespaced: true,
    state: {
        selectedProvince: null,
        selectedRejon: null,
        currentSelectorName: '',
        resource: [],
        addWarehouse: emptyAddWarehouse(),
        addSupportingWarehouse: emptyAddSupportingWarehouse(),
        address: emptyAddress(),
        hours: emptyHours(),
        directions: null,
        additionalNote: null,
        warehouseId: null,
        warehouseManagers: [],
        warehouseDraft: {},
        rejonHasWarehouse: null,
        warehousesPerLocation: [],
        availableWarehouses: [],
        availableWarehousesAmount: null,
        warehouseWasEdited: false,
        showDraftButton: true,
        pagination: paginationState()
    },
    getters: {
        getField,
        getCurrentSelectorName: (state) => state.currentSelectorName,
        getResource: (state) => state.resource,
        getWarehouseId: (state) => state.warehouseId,
        getWarehouseDraft: (state) => state.warehouseDraft,
        getAddress (state) {
            return getField(state.address);
        },
        getHours (state) {
            return getField(state.hours);
        },
        getBaseInfoField (state) {
            return getField(state.addWarehouse.baseInfo);
        },
        getWarehouseNameField (state) {
            return getField(state.addWarehouse.locationData);
        },
        getWarehouseAddressFields (state) {
            return getField(state.addWarehouse.locationData.address);
        },
        getWarehouseOpeningFields (state) {
            return getField(state.addWarehouse.locationData.openingHours);
        },
        getAdditionalParametersField (state) {
            return getField(state.addWarehouse.additionalParameters);
        },
        getWarehouseManagerField (state) {
            return getField(state.addWarehouse.warehouseManager);
        },
        getSelectedProvince: (state) => state.selectedProvince,
        getSelectedRejon: (state) => state.selectedRejon,
        getAddWarehouseData: (state) => state.addWarehouse,
        getWarehouseManagers: (state) => state.warehouseManagers,
        getAddSupportingWarehouseData: (state) => state.addSupportingWarehouse,
        getSupportingBaseInfoFields (state) {
            return getField(state.addSupportingWarehouse.baseInfo);
        },
        getSupportingWarehouseNameField (state) {
            return getField(state.addSupportingWarehouse.locationData);
        },
        getSupportingAdditionalParametersField (state) {
            return getField(state.addSupportingWarehouse.additionalParameters);
        },
        getSupportingWarehouseManagerField (state) {
            return getField(state.addSupportingWarehouse.warehouseManager);
        },
        getSupportingWarehouseAddressFields (state) {
            return getField(state.addSupportingWarehouse.locationData.address);
        },
        getSupportingWarehouseOpeningFields (state) {
            return getField(state.addSupportingWarehouse.locationData.openingHours);
        },
        getRejonHasWarehouse: (state) => state.rejonHasWarehouse,
        getWarehousesPerLocation: (state) => state.warehousesPerLocation,
        getAvailableWarehouses: (state) => state.availableWarehouses,
        getAvailableWarehousesAmount: (state) => state.availableWarehousesAmount,
        getWarehouseWasEdited: (state) => state.warehouseWasEdited,
        getShowDraftButton: (state) => state.showDraftButton,
        getPagination: (state) => state.pagination
    },
    mutations: {
        updateField,
        setCurrentSelectorName (state, payload) {
            state.currentSelectorName = payload;
        },
        setResource (state, payload) {
            state.resource = payload;
        },
        setWarehouseId (state, payload) {
            state.warehouseId = payload;
        },
        setWarehouseDraft (state, payload) {
            state.warehouseDraft = payload;
        },
        updateAddress (state, field) {
            updateField(state.address, field);
        },
        updateHours (state, field) {
            updateField(state.hours, field);
        },
        updateBaseInfoField (state, field) {
            updateField(state.addWarehouse.baseInfo, field);
        },
        updateWarehouseNameField (state, field) {
            updateField(state.addWarehouse.locationData, field);
        },
        updateWarehouseAddressFields (state, fields) {
            updateField(state.addWarehouse.locationData.address, fields);
        },
        updateWarehouseOpeningFields (state, fields) {
            updateField(state.addWarehouse.locationData.openingHours, fields);
        },
        updateAdditionalParametersField (state, field) {
            updateField(state.addWarehouse.additionalParameters, field);
        },
        updateWarehouseManagerField (state, field) {
            updateField(state.addWarehouse.warehouseManager, field);
        },
        updateSupportingBaseInfoField (state, field) {
            updateField(state.addSupportingWarehouse.baseInfo, field);
        },
        updateSupportingWarehouseNameField (state, field) {
            updateField(state.addSupportingWarehouse.locationData, field);
        },
        updateSupportingAdditionalParametersField (state, field) {
            updateField(state.addSupportingWarehouse.additionalParameters, field);
        },
        updateSupportingWarehouseManagerField (state, field) {
            updateField(state.addSupportingWarehouse.warehouseManager, field);
        },
        updateSupportingWarehouseAddressFields (state, fields) {
            updateField(state.addSupportingWarehouse.locationData.address, fields);
        },
        updateSupportingWarehouseOpeningFields (state, fields) {
            updateField(state.addSupportingWarehouse.locationData.openingHours, fields);
        },
        setDefaultAddWarehouseData (state) {
            const s = emptyAddWarehouse();
            Object.keys(s).forEach((key) => {
                state.addWarehouse[key] = s[key];
            });
        },
        setDefaultAddSupportingWarehouseData (state) {
            const s = emptyAddSupportingWarehouse();
            Object.keys(s).forEach((key) => {
                state.addSupportingWarehouse[key] = s[key];
            });
        },
        setDefaultAddress (state) {
            const s = emptyAddress();
            Object.keys(s).forEach((key) => {
                state.address[key] = s[key];
            });
        },
        setMergedAddress (state, data) {
            state.address = merge(state.address, data);
        },
        setDefaultHours (state) {
            const s = emptyHours();
            Object.keys(s).forEach((key) => {
                state.hours[key] = s[key];
            });
        },
        setMergedHours (state, data) {
            state.hours = merge(state.hours, data);
        },
        setDefaultDirections (state) {
            state.directions = null;
        },
        setMergedDirections (state, data) {
            state.directions = data;
        },
        setDefaultAdditionalNote (state) {
            state.additionalNote = null;
        },
        setMergedAdditionalNote (state, data) {
            state.additionalNote = data;
        },
        setRejonWarehouseData (state, payload) {
            state.addWarehouse = merge(state.addWarehouse, payload);
        },
        setSupportingWarehouseData (state, payload) {
            state.addSupportingWarehouse = merge(state.addSupportingWarehouse, payload);
        },
        setSelectedProvince (state, payload) {
            state.selectedProvince = payload;
        },
        setSelectedRejon (state, payload) {
            state.selectedRejon = payload;
        },
        setRejonHasWarehouse (state, payload) {
            state.rejonHasWarehouse = payload;
        },
        setWarehouseManagers (state, data) {
            state.warehouseManagers = data;
        },
        setWarehousesPerLocation (state, data) {
            state.warehousesPerLocation = data;
        },
        setAvailableWarehouses (state, data) {
            state.availableWarehouses = data;
        },
        setAvailableWarehousesAmount (state, data) {
            state.availableWarehousesAmount = data;
        },
        setWarehouseWasEdited (state, payload) {
            state.warehouseWasEdited = payload;
        },
        setShowDraftButton (state, payload) {
            state.showDraftButton = payload;
        },
        setPagination (state, data) {
            if (data.page != null) {
                state.pagination.page = data.page;
            }
            if (data.amount != null) {
                state.pagination.amount = data.amount;
            }
            if (Object.prototype.hasOwnProperty.call(data, 'filtersParams')) {
                state.pagination.filtersParams = data.filtersParams;
            }
            if (data.amountAll != null) {
                state.pagination.amountAll = data.amountAll;
            }
            localStorage.setItem(WAREHOUSE_PARAMS_STORAGE_NAME, JSON.stringify(state.pagination));
        },
        clearPagination (state) {
            localStorage.removeItem(WAREHOUSE_PARAMS_STORAGE_NAME);
            state.pagination = paginationState();
        }
    },
    actions: {
        async loadResource (context, id) {
            const currentEditionResponse = await edition.getCurrentEdition();
            const currentEditionName = currentEditionResponse.data.data.name;

            const resourceResponse = await apiSheet.getResourceSheet(id, currentEditionName);
            const sheetResource = resourceResponse.data.data.sheet_resource;

            context.commit('setResource', sheetResource);
        },
        createWarehouse (context, data) {
            return apiWarehouse.createWarehouse(data).then((data) => {
                context.commit('setWarehouseId', data.data.warehouse_id);
            });
        },
        updateWarehouse (context, data) {
            return apiWarehouse.updateWarehouse(data);
        },
        clearAddWarehouseData (context) {
            context.commit('setDefaultAddWarehouseData');
            context.commit('setDefaultAddress');
            context.commit('setDefaultHours');
            context.commit('setDefaultDirections');
            context.commit('setDefaultAdditionalNote');
            context.commit('setSelectedProvince', null);
            context.commit('setSelectedRejon', null);
        },
        clearAddSupportingWarehouseData (context) {
            context.commit('setDefaultAddSupportingWarehouseData');
            context.commit('setDefaultAddress');
            context.commit('setDefaultHours');
            context.commit('setDefaultDirections');
            context.commit('setDefaultAdditionalNote');
            context.commit('setSelectedProvince', null);
        },
        sendWarehouseDraft (context, payload) {
            return apiWarehouse
                .postWarehouseDraft(payload.id, { warehouse_draft_json: payload.warehouse_draft_json })
                .then(() => {
                    context.commit('setWarehouseDraft', payload.warehouse_draft_json);
                });
        },
        async getWarehouseDraft (context, payload) {
            const r = await apiWarehouse
                .getWarehouseDraft(payload)
                .then((data) => {
                    context.commit('setWarehouseDraft', data.data.data);
                    context.commit('setRejonWarehouseData', data.data.data);
                    context.commit('setMergedAddress', data.data.data.locationData.address);
                    context.commit('setMergedHours', data.data.data.locationData.openingHours);
                    context.commit('setMergedDirections', data.data.data.locationData.directions);
                    context.commit('setMergedAdditionalNote', data.data.data.additionalNote);
                })
                .catch((error) => {
                    if (error.request.status === HttpStatus.NOT_FOUND) {
                        context.commit('setWarehouseDraft', {});
                    }
                });
            return r;
        },
        async getSupportingWarehouseDraft (context, payload) {
            const r = await apiWarehouse
                .getWarehouseDraft(payload)
                .then((data) => {
                    context.commit('setWarehouseDraft', data.data.data);
                    context.commit('setSupportingWarehouseData', data.data.data);
                    context.commit('setMergedAddress', data.data.data.locationData.address);
                    context.commit('setMergedHours', data.data.data.locationData.openingHours);
                    context.commit('setMergedDirections', data.data.data.locationData.directions);
                    context.commit('setMergedAdditionalNote', data.data.data.additionalNote);
                })
                .catch((error) => {
                    if (error.request.status === HttpStatus.NOT_FOUND) {
                        context.commit('setWarehouseDraft', {});
                    }
                });
            return r;
        },
        removeWarehouseDraft (context, id) {
            return apiWarehouse.removeWarehouseDraft(id).then(() => {
                context.commit('setWarehouseDraft', {});
            });
        },
        setLocalIdToStorage (context, type) {
            localStorage.setItem('province', JSON.stringify(context.getters.getSelectedProvince));
            if (type === 1) {
                localStorage.setItem('rejon', JSON.stringify(context.getters.getSelectedRejon));
            }
        },
        getLocalIdFromLStorage (context, type) {
            const f = JSON.parse(localStorage.getItem('province'));
            context.commit('setSelectedProvince', f);
            if (type === 1) {
                const r = JSON.parse(localStorage.getItem('rejon'));
                context.commit('setSelectedRejon', r);
            }
        },
        removeLocalIdFromStorage (context, type) {
            localStorage.removeItem('province');
            if (type === 1) {
                localStorage.removeItem('rejon');
            }
        },
        existRejonWarehouse (context, id) {
            return apiWarehouse.getWarehouseExistInfo(id).catch((error) => {
                if (error.request.status === HttpStatus.CONFLICT) {
                    context.commit('setRejonHasWarehouse', true);
                } else if (error.request.status === HttpStatus.NOT_FOUND) {
                    context.commit('setRejonHasWarehouse', false);
                }
            });
        },
        getWarehouseManagers (context, data) {
            return apiWarehouse.getWarehouseManagers(data).then((data) => {
                context.commit('setWarehouseManagers', data.data.data);
            });
        },
        getWarehousesPerLocation (context, id) {
            return apiWarehouse.getWarehousesPerLocation(id).then(({ data }) => {
                context.commit('setWarehousesPerLocation', data.data);

                const warehouse = data.data[0];
                const response = {
                    baseInfo: {
                        ...warehouse.warehouse_data.base_info
                    },
                    locationData: {
                        warehouseName: warehouse.warehouse_name,
                        address: {
                            locationName: warehouse.location_name,
                            street: warehouse.street,
                            city: warehouse.city,
                            flatNumber: warehouse.flat_number,
                            placeNumber: warehouse.building_number,
                            province: warehouse.province.province_id,
                            rejon: warehouse.wio_struct_location_id,
                            postalCode: warehouse.postal_code
                        },
                        openingHours: {
                            ...warehouse.warehouse_data.location_data.opening_hours
                        },
                        directions: warehouse.warehouse_data.location_data.directions
                    },
                    additionalParameters: {
                        ...warehouse.warehouse_data.additional_parameters
                    },
                    warehouseManager: {
                        ...warehouse.warehouse_data.warehouse_manager,
                        manager: warehouse.warehouse_manager.id,
                        full_name: warehouse.warehouse_manager.full_name
                    },
                    additionalNote: warehouse.warehouse_data.additional_note
                };
                const street = response.locationData.address.street;
                if (street === null) {
                    response.locationData.address.street = false;
                }
                context.commit('setRejonWarehouseData', response);
                context.commit('setMergedAddress', response.locationData.address);
                context.commit('setMergedHours', response.locationData.openingHours);
                context.commit('setMergedDirections', response.locationData.directions);
                context.commit('setMergedAdditionalNote', response.additionalNote);
            });
        },
        getAllAvailableWarehouses (context) {
            const params = new URLSearchParams();
            params.append('limit', context.state.pagination.amount);
            const offsetNum = (context.state.pagination.page - 1) * context.state.pagination.amount;
            params.append('offset', offsetNum);
            const filterParams = context.state.pagination.filtersParams;
            if (filterParams != null) {
                if (filterParams.province) {
                    params.append('warehouse_province', filterParams.province);
                }
                if (filterParams.warehouse_type) {
                    params.append('warehouse_type', filterParams.warehouse_type);
                }
            }
            if (context.getters.getWarehouseWasEdited) {
                params.append('force_refresh', '1');
            }
            return apiWarehouse.getAllAvailableWarehouses(params).then((data) => {
                context.commit('setWarehouseWasEdited', false);
                context.commit('setAvailableWarehouses', data.data.data.data);
                context.commit('setAvailableWarehousesAmount', data.data.data.amount);
                return data;
            });
        }
    }
};
